import equal from 'fast-deep-equal';
import produce, { Draft, original } from 'immer';

import {
  EhrAppointmentGroupType,
  EhrAppointmentType,
  EhrSyncStateType,
} from 'types/appointment';

import {
  clearEhrAppointments,
  listEhrAppointments,
  toggleEhrAppointmentDisclosure,
} from './actions';

export type EhrAppointmentsError = {
  message: string | undefined;
  isRetryable: boolean;
};

export type EhrAppointmentsState = {
  leadId: string | undefined;
  loading: boolean;
  loaded: boolean;
  readRevision: number;
  error: EhrAppointmentsError | undefined;
  syncState: EhrSyncStateType | undefined;
  items: EhrAppointmentGroupType[];
  disclosures: Record<string, boolean>;
};

export const initialState: EhrAppointmentsState = {
  leadId: undefined,
  loading: false,
  loaded: false,
  readRevision: 0,
  error: undefined,
  syncState: undefined,
  items: [],
  disclosures: {},
};

const reconcileItems = (
  previous: EhrAppointmentGroupType[],
  incoming: EhrAppointmentGroupType[],
): EhrAppointmentGroupType[] => {
  const appointments = new Map<string, EhrAppointmentType>();
  for (const group of previous) {
    appointments.set(group.id, group);
    for (const booking of group.rescheduledTo) {
      appointments.set(booking.id, booking);
    }
  }
  const items = incoming.map((group) => {
    const existing = appointments.get(group.id);
    if (existing && equal(existing, group))
      return existing as EhrAppointmentGroupType;
    return {
      ...group,
      rescheduledTo: group.rescheduledTo.map((booking) => {
        const previousBooking = appointments.get(booking.id);
        return previousBooking && equal(previousBooking, booking)
          ? previousBooking
          : booking;
      }),
    };
  });
  return items.length === previous.length &&
    items.every((item, index) => item === previous[index])
    ? previous
    : items;
};

const reducer = produce(
  (draft: Draft<EhrAppointmentsState>, { type, payload }) => {
    switch (type) {
      case listEhrAppointments.TRIGGER:
        if (draft.leadId !== payload?.leadId) {
          draft.items = [];
          draft.disclosures = {};
          draft.syncState = undefined;
          draft.loaded = false;
          draft.readRevision = 0;
        }
        draft.leadId = payload?.leadId;
        draft.loading = true;
        draft.error = undefined;
        break;
      case listEhrAppointments.SUCCESS:
        if (draft.leadId !== payload?.leadId) break;
        draft.items = reconcileItems(
          original(draft.items) ?? [],
          payload?.items ?? [],
        );
        draft.readRevision += 1;
        draft.syncState = payload?.syncState;
        draft.loaded = true;
        break;
      case listEhrAppointments.FAILURE:
        draft.error = payload;
        draft.loaded = true;
        break;
      case listEhrAppointments.FULFILL:
        draft.loading = false;
        break;
      case toggleEhrAppointmentDisclosure.toString():
        draft.disclosures[payload] = !draft.disclosures[payload];
        break;
      case clearEhrAppointments.toString():
        Object.assign(draft, initialState);
        break;
      default:
        break;
    }
  },
  initialState,
);

export default reducer;
